/* Whale Tail Mug - scroll motion on GSAP (ScrollTrigger).
   Ported from docs/plans/whale-tail-apple/app.js for the Shopify theme.

   PORT NOTE: the prototype wrapped the whole document in ScrollSmoother
   (#smooth-wrapper/#smooth-content). On a themed product page that fights the
   Kalles header / footer / cart drawer / sticky nav, so we run on NATIVE scroll
   and drive everything with ScrollTrigger instead (pins/scrubs work the same;
   we lose only the inertia "glide", which the page does not depend on). GSAP +
   ScrollTrigger are vendored as theme assets (wtm-gsap.min.js /
   wtm-scrolltrigger.min.js) - no external CDN dependency at runtime. */
(() => {
  document.documentElement.dataset.appLoaded = "true";

  // Lenis smooth scroll — wired into the GSAP ticker so ScrollTrigger stays in sync.
  if (window.Lenis) {
    const lenis = new Lenis({ lerp: 0.08, smoothWheel: true, touchMultiplier: 1.5 });
    lenis.on("scroll", () => window.ScrollTrigger && ScrollTrigger.update());
    gsap.ticker.add(time => lenis.raf(time * 1000));
    gsap.ticker.lagSmoothing(0);
    window.__wtmLenis = lenis;
  }

  // ScrollSmoother is intentionally NOT loaded; ScrollTrigger is all we need.
  const hasGsap = window.gsap && window.ScrollTrigger;

  if (hasGsap) {
    gsap.registerPlugin(ScrollTrigger);
    if (window.ScrollSmoother) gsap.registerPlugin(ScrollSmoother);
    document.documentElement.dataset.gsap = "true";

    // Native smooth scroll only if a smooth-wrapper is present (it is not, on the
    // themed page) AND ScrollSmoother happens to be available. Otherwise null and
    // the page scrolls natively - ScrollTrigger choreography is unaffected.
    let smoother = null;
    if (window.ScrollSmoother && document.querySelector("#smooth-wrapper")) {
      smoother = ScrollSmoother.create({
        wrapper: "#smooth-wrapper",
        content: "#smooth-content",
        smooth: 1.7,
        effects: true,
        smoothTouch: 0.12,
        normalizeScroll: true
      });
    }

    // smooth-scroll in-page nav anchors (respect the transform if a smoother exists)
    document.querySelectorAll('a[href^="#"]').forEach(a => {
      const id = a.getAttribute("href");
      if (!id || id.length < 2) return;
      a.addEventListener("click", e => {
        const target = document.querySelector(id);
        if (!target) return;
        e.preventDefault();
        if (smoother) smoother.scrollTo(target, true, "top 70px");
        else target.scrollIntoView({ behavior: "smooth" });
      });
    });

    // Origin fan geometry (shared by the scroll tween + the reduced-motion static state).
    const fanStep = () => Math.max(80, Math.min(window.innerWidth * 0.155, 212));
    const fanD = (i) => Math.abs(i - 2);
    const fanEnd = {
      x: (i) => (i - 2) * fanStep(),
      y: (i) => fanD(i) * 16,
      rotation: (i) => (i - 2) * 7,
      scale: (i) => 1 - fanD(i) * 0.16,
      autoAlpha: (i) => 1 - fanD(i) * 0.06
    };

    const mm = gsap.matchMedia();

    // --- Motion ON ---
    mm.add("(prefers-reduced-motion: no-preference)", () => {
      // 1) Eased reveals - reuse the CSS .reveal/.in-view transition, fire on enter.
      ScrollTrigger.batch(".reveal", {
        start: "top 86%",
        once: true,
        onEnter: batch => batch.forEach(el => el.classList.add("in-view"))
      });

      // 1b) Hero headline: each word STAMPS in down the column.
      const stampWords = gsap.utils.toArray(".hero-stamp .stamp-word");
      if (stampWords.length) {
        gsap.set(stampWords, { autoAlpha: 0, scale: 1.32, filter: "blur(7px)", transformOrigin: "0% 60%" });
        gsap.to(stampWords, { autoAlpha: 1, scale: 1, filter: "blur(0px)", duration: 0.55, ease: "power3.out", stagger: 0.18, delay: 0.3 });
      }
      const kickerEnd = 0.3 + Math.max(1, stampWords.length) * 0.18 + 0.15;
      const heroKicker = document.querySelector(".hero-kicker");
      if (heroKicker) {
        gsap.set(heroKicker, { autoAlpha: 0, y: 12 });
        gsap.to(heroKicker, { autoAlpha: 1, y: 0, duration: 0.8, ease: "power2.out", delay: kickerEnd });
      }
      const heroFacts = gsap.utils.toArray(".depth-loop-hero .hero-foot > span");
      if (heroFacts.length) {
        gsap.set(heroFacts, { autoAlpha: 0, y: 10, filter: "blur(5px)" });
        gsap.to(heroFacts, { autoAlpha: 1, y: 0, filter: "blur(0px)", duration: 0.8, ease: "power2.out", stagger: 0.1, delay: kickerEnd + 0.2 });
      }

      // 1c) Origin line lands in two beats; the second beat softens + blurs away as it
      //     reaches the top and scrolls out - "...then it was gone."
      const oBeats = gsap.utils.toArray(".origin-title .o-beat");
      if (oBeats.length) {
        gsap.set(oBeats, { autoAlpha: 0, y: 18 });
        gsap.timeline({ scrollTrigger: { trigger: ".origin-center-copy", start: "top 80%", once: true } })
          .to(".o-beat-1", { autoAlpha: 1, y: 0, duration: 0.85, ease: "power2.out" })
          .to(".o-beat-2", { autoAlpha: 1, y: 0, duration: 0.85, ease: "power2.out" }, "+=0.55");
        gsap.fromTo(".origin-title .o-beat-2",
          { autoAlpha: 1, filter: "blur(0px)" },
          {
            autoAlpha: 0, filter: "blur(10px)", ease: "none", immediateRender: false,
            scrollTrigger: { trigger: ".origin-center-copy", start: "top 24%", end: "top -6%", scrub: 1.5 }
          }
        );
      }

      // 2) Origin fan-out: a real GSAP transform tween (reliable on iOS).
      const fanMugs = gsap.utils.toArray(".angle-spread-mug");
      if (fanMugs.length) {
        gsap.set(fanMugs, { xPercent: -50, yPercent: -50 });
        gsap.fromTo(fanMugs,
          { x: (i) => (i - 2) * fanStep() * 0.3, y: (i) => fanD(i) * 6, rotation: (i) => (i - 2) * 2, scale: 0.9, autoAlpha: 0.8 },
          {
            x: fanEnd.x, y: fanEnd.y, rotation: fanEnd.rotation, scale: fanEnd.scale, autoAlpha: fanEnd.autoAlpha,
            ease: "power2.out",
            scrollTrigger: {
              trigger: ".angle-spread-figure",
              start: "top 88%",
              end: "center 54%",
              scrub: 1.5,
              invalidateOnRefresh: true
            }
          }
        );
      }

      // 3) Glaze "Pick your Vibe" rail: scrub the rail horizontally first -> last, each mug
      //    riding a gentle arc. NO pin / no scroll-lock; vertical scroll is never trapped.
      const wheel = document.querySelector(".glaze-wheel");
      const stage = document.querySelector(".glaze-fan-stage");
      const section = document.querySelector(".glaze-process-section");
      if (wheel && stage && section) {
        const shift = () => Math.max(0, wheel.scrollWidth - stage.offsetWidth);
        const items = gsap.utils.toArray(".glaze-wheel-item");
        let railBase = 0, centers = [];
        const cacheArc = () => {
          railBase = wheel.getBoundingClientRect().left - (gsap.getProperty(wheel, "x") || 0);
          centers = items.map(it => it.offsetLeft + it.offsetWidth / 2);
        };
        const applyArc = () => {
          const vw = window.innerWidth;
          const A = Math.max(20, Math.min(vw * 0.038, 46)); // arc height (gentler)
          const wx = gsap.getProperty(wheel, "x") || 0;
          for (let i = 0; i < items.length; i++) {
            let d = (railBase + centers[i] + wx - vw / 2) / (vw / 2);
            if (d < -1.5) d = -1.5; else if (d > 1.5) d = 1.5;
            gsap.set(items[i], { y: Math.round(-A * (1 - d * d)) });
          }
        };
        gsap.fromTo(wheel,
          { x: () => -shift() * 0.06 },
          {
            x: () => -shift() * 0.94,
            ease: "none",
            onUpdate: applyArc,
            scrollTrigger: {
              trigger: section,
              start: "top bottom",
              end: "bottom top",
              scrub: 1.5,
              invalidateOnRefresh: true,
              onRefresh: () => { cacheArc(); applyArc(); }
            }
          }
        );
        cacheArc();
        applyArc();
      }

      // 4) Final CTA: the scattered cluster blurs + fades, leaving the centered feature mug.
      const ctaSection = document.querySelector(".final-cta");
      const ctaMugs = gsap.utils.toArray(".cta-mug:not(.cta-mug-3)"); // keep the center mug
      if (ctaSection && ctaMugs.length) {
        const shadow = "drop-shadow(0 16px 26px rgba(20, 40, 60, .20))";
        gsap.fromTo(ctaMugs,
          { autoAlpha: 1, filter: "blur(0px) " + shadow },
          {
            autoAlpha: 0,
            filter: "blur(16px) " + shadow,
            ease: "none",
            stagger: { each: 0.05, from: "edges" },
            scrollTrigger: {
              trigger: ctaSection,
              start: "top 72%",
              end: "center 44%",
              scrub: 1.5
            }
          }
        );
      }
    });

    // --- Motion OFF (reduced) ---
    mm.add("(prefers-reduced-motion: reduce)", () => {
      document.querySelectorAll(".reveal").forEach(el => el.classList.add("in-view"));
      gsap.set(".angle-spread-mug", Object.assign({ xPercent: -50, yPercent: -50 }, fanEnd));
      gsap.set(".glaze-wheel", { x: 0 });
    });

    // Recompute pin/scrub distances once late assets (mug webp) settle.
    window.addEventListener("load", () => ScrollTrigger.refresh());
  } else {
    // No GSAP (or it failed to load): reveal everything so nothing is stuck hidden.
    document.querySelectorAll(".reveal").forEach(el => el.classList.add("in-view"));
    const f = document.querySelector(".angle-spread-figure");
    if (f) f.style.setProperty("--angle-delay", "-1s");
  }

  // --- Accordions (buy panel). Finish swatches are wired to real variants in the
  //     buy section's own script, so the prototype's [data-image] swatch handler is
  //     intentionally dropped here. ---
  try {
    if (!window.__wtmAcc) {
      window.__wtmAcc = true;
      for (const button of document.querySelectorAll(".accordion button")) {
        button.addEventListener("click", () => {
          const item = button.closest(".accordion");
          const expanded = item.classList.toggle("open");
          button.setAttribute("aria-expanded", expanded ? "true" : "false");
          const sign = button.querySelector("span");
          if (sign) sign.textContent = expanded ? "-" : "+";
        });
      }
    }
  } catch (error) {
    document.documentElement.dataset.appError = error.message;
  }
})();
